import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Link, useRouterState } from '@tanstack/react-router';
import { Maximize2, MessageSquarePlus, Sparkles, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';
import { ChatView } from './chat-view';
import { usePageContext } from './page-context';

/* ---------------------------------------------------------------------------
   הכפתור הצף והמגירה. בכל מסך מחובר (AppShell — לא בדפים הציבוריים), חוץ
   מ-/assistant עצמו ומדף הבית (שם העוזר במרכז — HomeAssistant). בדסקטופ — מגירה בצד ה-end; במובייל — מסך מלא.
   Radix מטפל במלכודת הפוקוס, Escape והחזרת הפוקוס לכפתור.
   --------------------------------------------------------------------------- */

const LAST_KEY = 'craftmind.assistant.last';

/** השיחה האחרונה — נוחות לצופה הזה בלבד; בלי אחסון (מצב פרטי) מתחילים שיחה חדשה. */
export const lastConversation = {
  get: (): string | null => {
    try {
      return localStorage.getItem(LAST_KEY);
    } catch {
      return null;
    }
  },
  set: (id: string | null): void => {
    try {
      if (id) localStorage.setItem(LAST_KEY, id);
      else localStorage.removeItem(LAST_KEY);
    } catch {
      /* מצב פרטי */
    }
  },
};

export function AssistantLauncher() {
  const { t } = useTranslation('assistant');
  const { session } = useAuth();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const context = usePageContext();
  const [open, setOpen] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(() => lastConversation.get());

  // בדף הבית העוזר כבר במרכז המסך (HomeAssistant) — אין צורך בכפתור צף.
  if (
    !session ||
    session.user.role === 'FIELD' ||
    pathname === '/' ||
    pathname.startsWith('/assistant')
  )
    return null;

  const choose = (id: string | null) => {
    setConversationId(id);
    lastConversation.set(id);
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
      <DialogPrimitive.Trigger asChild>
        <button
          type="button"
          aria-label={t('launcher.open')}
          data-track="assistant.open"
          className={cn(
            'fixed bottom-4 end-4 z-30 grid size-14 place-items-center rounded-full bg-accent text-fg-on-accent shadow-lg',
            'transition-transform hover:scale-105 focus-visible:outline-offset-2',
          )}
        >
          <Sparkles className="size-6" aria-hidden />
        </button>
      </DialogPrimitive.Trigger>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/30 data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className={cn(
            'fixed inset-0 z-50 flex flex-col bg-canvas shadow-lg',
            'sm:inset-y-0 sm:start-auto sm:end-0 sm:w-[440px] sm:border-s sm:border-border',
          )}
        >
          <header className="flex h-14 shrink-0 items-center gap-1 border-b border-border bg-surface px-3">
            <Sparkles className="size-4 text-accent" aria-hidden />
            <DialogPrimitive.Title className="flex-1 text-sm font-semibold text-fg">
              {t('title')}
            </DialogPrimitive.Title>
            <Button
              variant="ghost"
              size="icon"
              className="size-9"
              aria-label={t('launcher.newChat')}
              onClick={() => choose(null)}
            >
              <MessageSquarePlus aria-hidden />
            </Button>
            <Button variant="ghost" size="icon" className="size-9" asChild>
              <Link
                to="/assistant"
                search={conversationId ? { c: conversationId } : {}}
                aria-label={t('launcher.fullPage')}
                onClick={() => setOpen(false)}
              >
                <Maximize2 aria-hidden />
              </Link>
            </Button>
            <DialogPrimitive.Close asChild>
              <Button
                variant="ghost"
                size="icon"
                className="size-9"
                aria-label={t('launcher.close')}
              >
                <X aria-hidden />
              </Button>
            </DialogPrimitive.Close>
          </header>
          <ChatView
            conversationId={conversationId}
            onConversation={(c) => choose(c.id)}
            onMissing={() => choose(null)}
            context={context}
          />
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
